import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UploadDialog from "@/components/modals/UploadDialog";
import { downloadTemplate, downloadCsv } from "@/lib/csv";
import { FileDown, UploadCloud, Download, FileSpreadsheet, CheckCircle2, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const ENTITIES = [
  { key: "rate-cards", title: "Rate Cards", endpoint: "/rate-cards", queryKey: "rate-cards",
    columns: ["category", "basis", "location", "unit", "y1_price", "y2_price"],
    help: "Delivery, storage, movement, picking and activity rates per depot and basis (Full Pallet / Case). A duplicate is the same category + location + basis." },
  { key: "products", title: "Products", endpoint: "/products", queryKey: "products",
    columns: ["product_code", "description", "product_cat", "invoiced_price", "delta_price", "total_units", "total_shipthru"],
    help: "Product catalogue with invoiced price and delta price. A duplicate is the same product code." },
  { key: "dap-pricing", title: "DAP Pricing by SKU", endpoint: "/dap-pricing", queryKey: "dap-pricing",
    columns: ["product_code", "description", "category", "site", "basis", "picking", "storage", "movement", "delivery", "activity", "other"],
    help: "Delivered-at-place price broken down per SKU (picking, storage, movement, delivery, activity, other). A duplicate is the same product code." },
  { key: "cost-of-goods", title: "Cost of Goods", endpoint: "/cost-of-goods", queryKey: "cost-of-goods",
    columns: ["period", "site", "cust_group", "product_description", "product_cat", "invoices", "quantity", "cogs", "picks"],
    help: "Manual invoices / cost of goods by period and site. A duplicate is the same period + customer group + product." },
  { key: "savings", title: "Savings Tracker", endpoint: "/savings", queryKey: "savings",
    columns: ["period", "site", "gainshare_pct", "weeks_in_period", "opening_balance", "savings_in_period", "closing_balance"],
    help: "Period savings and gainshare. A duplicate is the same period." },
  { key: "service-credits", title: "Service Credits", endpoint: "/service-credits", queryKey: "service-credits",
    columns: ["number", "service_standard", "weighting", "monthly_credit", "annual_credit", "target", "compliance_rate", "score", "credit_due", "recorded_by"],
    help: "SLA service standards and credits. A duplicate is the same number." },
  { key: "pulled-forward", title: "Pulled Forward Savings", endpoint: "/pulled-forward", queryKey: "pulled-forward",
    columns: ["month", "site", "opening_balance", "pulled_forward", "savings_in_period", "closing_balance"],
    help: "Pulled forward savings by month and site. A duplicate is the same month + site." },
  { key: "volumes", title: "Site Volumes", endpoint: "/volumes", queryKey: "volumes",
    columns: ["period", "site", "basis", "picks", "pallets", "deliveries"],
    help: "Actual activity volumes per period and site — number of picks, pallets stored and deliveries. These drive volume-based picking, storage and delivery costs on the Site Summary. A duplicate is the same period + site.",
    hints: {
      period: "Period label, e.g. PERIOD 0127",
      site: "Depot name, e.g. IPL Normanton",
      basis: "Case or Full Pallet",
      picks: "Number of picks in the period",
      pallets: "Pallets stored in the period",
      deliveries: "Number of pallet deliveries (drops)",
    } },
];

function EntityCard({ meta, onUpload }) {
  const { data = [], isLoading } = useQuery({ queryKey: [meta.queryKey], queryFn: () => api.get(meta.endpoint).then((r) => r.data) });

  const exportData = () => {
    if (!data.length) { downloadTemplate(meta.key, meta.columns); return; }
    downloadCsv(`epgroup-${meta.key}.csv`, meta.columns, data);
  };

  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800 flex flex-col" data-testid={`import-card-${meta.key}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display font-semibold text-slate-900 dark:text-white">{meta.title}</h3>
            <p className="text-xs text-slate-400 flex items-center gap-1">
              {isLoading ? "…" : <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> {data.length} records</>}
            </p>
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-3 flex-1">{meta.help}</p>
      <div className="mt-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1">
          Expected columns
          {meta.hints && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild><HelpCircle className="w-3 h-3 text-slate-400 cursor-help" data-testid={`hint-${meta.key}`} /></TooltipTrigger>
                <TooltipContent className="max-w-xs">Hover any column below for what to put in it.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
        <div className="flex flex-wrap gap-1">
          {meta.columns.map((c) => {
            const chip = <span className={`text-[11px] font-mono-num bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded px-1.5 py-0.5 ${meta.hints?.[c] ? "cursor-help border-b border-dotted border-slate-400" : ""}`}>{c}</span>;
            return meta.hints?.[c] ? (
              <TooltipProvider key={c} delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>{chip}</TooltipTrigger>
                  <TooltipContent>{meta.hints[c]}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : <span key={c}>{chip}</span>;
          })}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" variant="outline" onClick={() => downloadTemplate(meta.key, meta.columns)} data-testid={`template-${meta.key}`}><FileDown className="w-4 h-4 mr-1.5" /> Template</Button>
        <Button size="sm" variant="outline" onClick={exportData} data-testid={`export-${meta.key}`}><Download className="w-4 h-4 mr-1.5" /> Export</Button>
        <Button size="sm" onClick={() => onUpload(meta)} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid={`upload-${meta.key}`}><UploadCloud className="w-4 h-4 mr-1.5" /> Import</Button>
      </div>
    </Card>
  );
}

function AuditTrail() {
  const { data = [] } = useQuery({ queryKey: ["import-logs"], queryFn: () => api.get("/import-logs").then((r) => r.data) });
  const fmt = (t) => { try { return new Date(t).toLocaleString("en-GB"); } catch (e) { return t; } };
  const exportLogs = () => downloadCsv("epgroup-import-audit-trail.csv", ["created_at", "user_email", "entity", "inserted", "overwritten", "skipped", "total"], data);
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-white">Import Audit Trail</h3>
        <Button variant="outline" size="sm" onClick={exportLogs} disabled={!data.length} data-testid="audit-export-button"><Download className="w-4 h-4 mr-1.5" /> Export log</Button>
      </div>
      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-trail-table">
            <thead><tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <th className="px-4 py-3">When</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Data set</th>
              <th className="px-4 py-3 text-right">Added</th><th className="px-4 py-3 text-right">Overwritten</th><th className="px-4 py-3 text-right">Skipped</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-mono-num text-slate-600">{fmt(l.created_at)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{l.user_email || "—"}</td>
                  <td className="px-4 py-2.5 font-medium">{l.entity}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-emerald-600">{l.inserted}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-amber-600">{l.overwritten}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-slate-500">{l.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.length === 0 && <div className="py-10 text-center text-sm text-slate-500">No imports yet — imported files will be logged here.</div>}
        </div>
      </Card>
    </div>
  );
}

export default function ImportExport() {
  const qc = useQueryClient();
  const [uploadMeta, setUploadMeta] = useState(null);

  const downloadAllTemplates = () => ENTITIES.forEach((m) => downloadTemplate(m.key, m.columns));

  return (
    <div>
      <PageHeader title="Import & Export Centre" subtitle="Guided import for every file the system needs. Download a template, fill it in, then import — duplicates are always checked.">
        <Button variant="outline" onClick={downloadAllTemplates} data-testid="download-all-templates"><FileDown className="w-4 h-4 mr-1.5" /> All templates</Button>
      </PageHeader>

      <Card className="p-5 mb-6 border-blue-200 bg-blue-50/60 dark:bg-blue-950/30">
        <div className="flex gap-3">
          <HelpCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 dark:text-slate-200">
            <p className="font-medium mb-1">How importing works</p>
            <ol className="list-decimal ml-4 space-y-0.5 text-slate-600 dark:text-slate-300">
              <li>Download the template for the data you want to load (headers match exactly what's expected).</li>
              <li>Paste your data under the headers and save as .csv or .xlsx.</li>
              <li>Click <strong>Import</strong> — you'll see which rows are new and which are duplicates, and can skip or overwrite each one.</li>
              <li>Confirm to apply. Use <strong>Export</strong> any time to download the current data as a backup.</li>
            </ol>
            <p className="mt-2 text-slate-500">The Period Calendar (FY27–FY31) is generated automatically — no import needed.</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {ENTITIES.map((m) => <EntityCard key={m.key} meta={m} onUpload={setUploadMeta} />)}
      </div>

      <AuditTrail />

      {uploadMeta && (
        <UploadDialog
          open={!!uploadMeta}
          onOpenChange={(v) => !v && setUploadMeta(null)}
          entity={uploadMeta.key}
          columns={uploadMeta.columns}
          onDone={() => { setUploadMeta(null); qc.invalidateQueries({ queryKey: ["import-logs"] }); }}
        />
      )}
    </div>
  );
}
